import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { toast } from '../components/ui/Toast';
import type { CreateMedidaFotoInput } from '@shared/validation/project-schemas';

export interface MedidaFotoDTO {
  id: string;
  medida_id: string | null;
  project_id: string;
  s3_key: string;
  filename: string;
  mime_type: string;
  size_bytes: number | null;
  created_at: string;
}

const medidaFotoKeys = {
  list: (projectId: string) => ['medida-fotos', projectId] as const,
};

export function useMedidaFotos(projectId: string | undefined) {
  return useQuery({
    queryKey: medidaFotoKeys.list(projectId ?? ''),
    queryFn: async () => {
      const response = await api.get<{ data: MedidaFotoDTO[] }>(
        `/projects/${projectId}/medida-fotos`,
      );
      return response.data.data;
    },
    enabled: !!projectId,
  });
}

export function useCreateMedidaFoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: CreateMedidaFotoInput;
    }) => {
      const response = await api.post<{ data: MedidaFotoDTO }>(
        `/projects/${projectId}/medida-fotos`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: medidaFotoKeys.list(variables.projectId) });
    },
  });
}

export function useDeleteMedidaFoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { fotoId: string; projectId: string }) => {
      await api.delete(`/medida-fotos/${vars.fotoId}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: medidaFotoKeys.list(variables.projectId) });
      toast('success', 'Foto eliminada');
    },
  });
}
